/**
 * Side by side — `docs/new-request.md` §10.9, Board 7 B, Board 15 D.
 *
 * Up to three people for the same new place and length, each from their own
 * start: their best times as cards, their nearest stop day by day as a grid
 * (each person's best day outlined, the shown day's column ringed), and the
 * shown day as one 146 px column each under a shared time axis, where a green
 * gap books that person at that time.
 */
import type { ReactNode } from 'react'
import {
  gradeRank,
  dayShort, milesWords, weekdayShort, type CalendarPerson, type CompareView, type Opening, type Segment,
  type StripCell,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { Avatar } from '../../components/primitives.js'
import type { PersonRef } from '../sheet-context.js'
import { Eyebrow, Legend, PersonBestCard, SMALL_INITIALS, notCoveringWords } from './bits.js'
import { DayColumn, HourLines, axisHeight, axisOf, type NewBlockWords } from './day-timeline.js'
import { TONE_TEXT } from './tones.js'
import type { Shown } from './use-schedule.js'

const refOf = (person: CalendarPerson): PersonRef =>
  ({ id: person.id, name: person.name, firstName: person.firstName })

const dayOfMonth = (date: string): number => Number(date.slice(8, 10))

/** A grid cell's number: *4.3*, *12*, *—*, or *off*. */
function cellValue(cell: StripCell): { text: string; tone: string } {
  if (cell.fit.kind === 'off') return { text: 'off', tone: 'text-text-faint' }
  if (cell.nearest === null) return { text: '—', tone: 'text-text-faint' }
  return { text: milesWords(cell.nearest).replace(/ mi$/, ''), tone: TONE_TEXT[cell.tone] }
}

/** *1 fit*, *2 fit*, *full*, *open*; nothing on a day off. */
function cellFit(cell: StripCell): { text: string; tone: string } {
  switch (cell.fit.kind) {
    case 'fits': return { text: `${cell.fit.count} fit`, tone: 'text-accent-dark' }
    case 'full': return { text: 'full', tone: 'text-text-faint' }
    case 'open': return { text: 'open', tone: 'text-text-faint' }
    case 'off': return { text: '', tone: 'text-text-faint' }
  }
}

const dimmed = (cell: StripCell): boolean => cell.fit.kind === 'off' || cell.fit.kind === 'full'

export interface SideBySideProps {
  view: CompareView
  shown: Shown | null
  words: NewBlockWords
  divisionName: string | null
  segment: Segment
  hasPlace: boolean
  /** A best-time card or a green gap: this person, this time. */
  onPick: (person: CalendarPerson, opening: { date: string; start: number; startsAt?: string }) => void
  /** A grid cell: show that day. */
  onDay: (date: string) => void
}

/** Side by side on a desktop or an iPad (Board 7 B; the same 560 aside fits three in portrait's 620). */
export function SideBySide({ view, shown, words, divisionName, segment, hasPlace, onPick, onDay }: SideBySideProps) {
  const axis = axisOf(view.people.map((column) => column.day.plan), true)
  const today = view.days[0] ?? ''
  const isSelected = (person: CalendarPerson, opening: Opening | null) => Boolean(opening && shown
    && shown.personId === person.id && shown.date === opening.date && shown.start === opening.start)

  return (
    <div className="flex flex-col gap-2.5">
      <Eyebrow>Each person’s best time</Eyebrow>
      <div className="flex gap-2">
        {/* Best grade first, then the least added drive (Board 7 B: Tom, Luis, Hannah). */}
        {[...view.people].sort((a, b) =>
          (a.best ? gradeRank(a.best.grade) : 9) - (b.best ? gradeRank(b.best.grade) : 9)
          || (a.best?.added ?? 999) - (b.best?.added ?? 999)).map((column) => (
          <PersonBestCard
            key={column.person.id}
            person={refOf(column.person)}
            opening={column.best}
            selected={isSelected(column.person, column.best)}
            notCovering={notCoveringWords(column.person.covers, divisionName, segment)}
            onPick={() => column.best && onPick(column.person, column.best)}
          />
        ))}
      </div>

      <div className="flex items-center gap-1.5">
        <Eyebrow>Nearest stop, day by day</Eyebrow>
        <span className="flex-1" />
        <span className="inline-flex items-center gap-[5px] text-[11px] text-text-muted">
          <i className="inline-block h-3 w-3.5 rounded-tag bg-green-band-bg shadow-[inset_0_0_0_1.5px_rgb(var(--color-accent-line))]" />
          their best day
        </span>
      </div>
      <div className="w-[524px] max-w-full" role="grid" aria-label="Nearest stop, day by day">
        <div className="flex items-end" role="row">
          <div className="w-16 flex-none" />
          {view.days.map((date) => (
            <div key={date} role="columnheader"
              className={cn('w-[46px] flex-none pb-1 text-center leading-[1.1]',
                date === view.date && 'rounded-t-button border-[1.5px] border-b-0 border-accent bg-surface pt-1')}>
              <div className="text-[9.5px] font-semibold uppercase tracking-[.03em] text-text-muted">
                {date === today ? 'Today' : weekdayShort(date)}
              </div>
              <b className="text-[12.5px] font-bold text-text">{dayOfMonth(date)}</b>
            </div>
          ))}
        </div>
        {view.people.map((column, row) => (
          <div key={column.person.id} className="flex items-center border-t border-line-faint" role="row">
            <div className="flex w-16 flex-none items-center gap-[5px] text-[12px] font-semibold text-text">
              <Avatar name={column.person.name} size={20} className={SMALL_INITIALS} />{column.person.firstName}
            </div>
            {column.strip.map((cell) => {
              const value = cellValue(cell)
              const fit = cellFit(cell)
              const on = cell.date === view.date
              const best = cell.date === column.bestDay
              const lastRow = row === view.people.length - 1
              return (
                <button
                  key={cell.date}
                  type="button"
                  role="gridcell"
                  aria-selected={on}
                  onClick={() => onDay(cell.date)}
                  className={cn('flex h-10 w-[46px] flex-none items-center justify-center',
                    on && 'border-x-[1.5px] border-accent bg-surface',
                    on && lastRow && 'rounded-b-button border-b-[1.5px]',
                    dimmed(cell) && 'opacity-50')}
                >
                  <span className={cn('flex h-[34px] w-9 flex-col items-center justify-center rounded-button leading-[normal]',
                    best && 'bg-green-band-bg shadow-[inset_0_0_0_1.5px_rgb(var(--color-accent-line))]')}>
                    <b className={cn('text-[12px] font-bold', hasPlace ? value.tone : 'text-text-faint')}>
                      {hasPlace || cell.fit.kind === 'off' ? value.text : '—'}
                    </b>
                    <span className={cn('text-[9.5px] font-semibold', fit.tone)}>{fit.text}</span>
                  </span>
                </button>
              )
            })}
          </div>
        ))}
      </div>

      <div className="mt-1 flex items-center gap-1.5">
        <Eyebrow>{dayShort(view.date)} · click a green gap to book</Eyebrow>
        <span className="flex-1" />
        <Legend columns={4} />
      </div>
      <div className="flex">
        <div className="w-11 flex-none" />
        {view.people.map((column) => {
          const cell = column.strip.find((one) => one.date === view.date)
          return (
            <div key={column.person.id} className="mr-2 flex w-[146px] flex-none items-center gap-1.5 text-[12px] font-bold text-text">
              <Avatar name={column.person.name} size={20} className={SMALL_INITIALS} />
              {column.person.firstName}
              <span className="flex-1" />
              {hasPlace && cell && cell.nearest !== null && (
                <span className={cn('text-[10.5px] font-bold', TONE_TEXT[cell.tone])}>{milesWords(cell.nearest)}</span>
              )}
            </div>
          )
        })}
      </div>
      <div className="relative" style={{ width: 44 + 154 * view.people.length - 8, height: axisHeight(axis) }}>
        <HourLines axis={axis} inset={40} labelSize="text-[10px]" />
        {view.people.map((column, index) => (
          <div key={column.person.id} className="absolute top-0" style={{ left: 44 + index * 154, height: axisHeight(axis) }}>
            <DayColumn
              plan={column.day.plan}
              person={column.person}
              minutes={view.minutes}
              chosenStart={shown && shown.personId === column.person.id && shown.date === column.day.date
                ? shown.start : null}
              axis={axis}
              title={words.title}
              onPick={(start) => onPick(column.person, { date: column.day.date, start })}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * The phone's grid (Board 15 D): six days in view, *swipe for more days*;
 * a tapped cell opens that person's day.
 */
export function PhoneNearnessGrid({ view, hasPlace, onCell }: {
  view: CompareView
  hasPlace: boolean
  onCell: (person: CalendarPerson, date: string) => void
}) {
  const today = view.days[0] ?? ''
  const head = (children: ReactNode, key: string) => (
    <div key={key} className="w-[52px] flex-none text-center leading-[1.1]">{children}</div>
  )
  return (
    <div className="no-scrollbar -mx-4 overflow-x-auto px-4" role="grid" aria-label="Nearest stop">
      <div className="inline-flex flex-col">
        <div className="flex gap-[3px]" role="row">
          <div className="w-[30px] flex-none" />
          {view.days.map((date) => head(
            <>
              <div className="text-[9.5px] font-semibold uppercase text-text-muted">
                {date === today ? 'Today' : weekdayShort(date)}
              </div>
              <b className="text-[13px] font-bold text-text">{dayOfMonth(date)}</b>
            </>, date))}
        </div>
        {view.people.map((column) => (
          <div key={column.person.id} className="flex items-center gap-[3px] border-t border-line-faint py-1" role="row">
            <div className="w-[30px] flex-none"><Avatar name={column.person.name} size={24} /></div>
            {column.strip.map((cell) => {
              const value = cellValue(cell)
              const fit = cellFit(cell)
              return (
                <button
                  key={cell.date}
                  type="button"
                  role="gridcell"
                  aria-selected={cell.date === view.date}
                  onClick={() => onCell(column.person, cell.date)}
                  className={cn('flex h-11 w-[52px] flex-none flex-col items-center justify-center rounded-[9px]',
                    cell.date === column.bestDay
                      && 'bg-green-band-bg shadow-[inset_0_0_0_1.5px_rgb(var(--color-accent-line))]',
                    cell.date === view.date && 'outline outline-[1.5px] outline-accent',
                    dimmed(cell) && 'opacity-50')}
                >
                  <b className={cn('text-[13px] font-bold', hasPlace ? value.tone : 'text-text-faint')}>
                    {hasPlace || cell.fit.kind === 'off' ? value.text : '—'}
                  </b>
                  <span className={cn('text-[10px] font-semibold', fit.tone)}>{fit.text}</span>
                </button>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
